import React from 'react';

import { Link } from 'react-router-dom';

function UsingDeclarationInProposals() {
  return (
    <>

      <div className="bg-[#F9FAFB] min-h-screen">
        {/* Header Section */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 py-16 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-[#102A43] mb-6">Using AASC Accountability Declaration in Client Proposals</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Accurately communicating your declaration status builds trust and demonstrates operational maturity. Here is how to do it correctly.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-12 space-y-12">

          {/* Section 1: Introduction */}
          <section className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-[#102A43]">1. Introduction</h2>
            </div>
            <div className="p-8 text-[#64748B] leading-relaxed">
              <p>
                declaration is a trust signal. When included in proposals, it differentiates your agency by proving you have established <strong>documented conformance</strong> to independent standards. However, precision is required. You must avoid overstating the declaration's scope or meaning.
              </p>
            </div>
          </section>

          {/* Section 2: Copy-Ready Paragraph */}
          <section className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 px-8 py-4">
              <h2 className="text-xl font-bold text-white">2. Copy-Ready Language</h2>
            </div>
            <div className="p-8">
              <p className="text-[#64748B] mb-4">You may use the following paragraph in your "About Us," "Methodology," or "Governance" sections of a proposal:</p>
              
              <div className="bg-[#F9FAFB] p-6 rounded-lg border border-[#E2E8F0] font-mono text-sm text-gray-800 leading-relaxed">
                "[Agency Name] has declared with the AI Accountability Standards Council (AASC) for [Insert Scope, e.g., 'our Enterprise Automation Division']. This declaration verifies our documented conformance to baseline control methodologies for human oversight and accountability (Standards FOUND-HITL-1.0 and FOUND-ACCT-1.0). Verification ID: [Your ID]. Verify our current status at: /verify-credential/[Your ID]."
              </div>
              
              <p className="text-sm text-gray-500 mt-4 italic">
                Note: Replace bracketed text with your specific details. Ensure the link points directly to your registry entry.
              </p>
            </div>
          </section>

          {/* Section 3: DO and DON'T List */}
          <section className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-[#102A43]">3. Usage Guidelines</h2>
            </div>
            <div className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-bold text-green-700 mb-3 uppercase tracking-wide">DO</h3>
                  <ul className="space-y-3 text-sm text-[#64748B]">
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      Link directly to your public registry entry.
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      Specify the scope of declaration (e.g., specific division vs. whole company).
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      Reference specific standards (e.g., "Tier 1 Baseline Controls").
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      Clarify that this is for operational methodology, not software code.
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-bold text-red-700 mb-3 uppercase tracking-wide">DON'T</h3>
                  <ul className="space-y-3 text-sm text-[#64748B]">
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">✕</span>
                      Claim declaration guarantees ROI or error-free performance.
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">✕</span>
                      Imply this constitutes legal compliance or regulatory approval (e.g., "EU AI Act Compliant").
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">✕</span>
                      Use the logo on products or deliverables that are outside the declared scope.
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">✕</span>
                      Alter the declaration mark.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Verification Link */}
          <section className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-[#102A43]">4. Verification Link</h2>
            </div>
            <div className="p-8 text-[#64748B] leading-relaxed">
              <p className="mb-4">
                Providing a functional verification link is mandatory for digital proposals. This allows clients to instantly validate your standing.
              </p>
              <p className="mb-2"><strong>Format:</strong></p>
              <code className="bg-gray-100 px-2 py-1 rounded text-sm block mb-4">/verify-credential/[YOUR-ID]</code>
              <p>
                Clients value transparency. Making verification easy demonstrates confidence in your compliance.
              </p>
            </div>
          </section>

          {/* Section 5: Questions */}
          <section className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-[#102A43]">5. Questions?</h2>
            </div>
            <div className="p-8 text-[#64748B] leading-relaxed">
              <p className="mb-4">
                If you are unsure whether a specific phrasing complies with our usage policy, please contact the Council before submitting your proposal.
              </p>
              <p>
                <strong>Contact:</strong> <a href="mailto:support@aiaccountabilitycouncil.com" className="text-[#2F80ED] hover:underline">support@aiaccountabilitycouncil.com</a>
              </p>
              <div className="mt-6">
                <Link to="/declaration-mark-usage-policy" className="text-[#2F80ED] hover:underline font-medium">
                  Read full Declaration Badge Usage Policy
                </Link>
              </div>
            </div>
          </section>

        </div>
      </div>
    </>
  );
}

export default UsingDeclarationInProposals;